<template>
  <article class='fiat-account-details-wrapper' v-if="account">
    <h3>Account Type</h3>
    <dl>
      <div>
        <dt>Account type</dt>
        <dd>{{ accountType }}</dd>
      </div>
      <div v-if="account.external_id || isEditMode">
        <dt>Your Internal Reference</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.external_id" v-model="editedAccount.external_id"></vs-input></label></dd>
        <dd v-else>{{ account.external_id }}</dd>
      </div>
      <div v-if="!isCompany">
        <dt>Account KYC Status</dt>
        <dd>
          <tag>{{ account.verification_status }}</tag>
          <button class="btn icon-btn plus" v-if="shouldDisplayKycUploadButton" @click="handleKycClick()">Add KYC document</button>
        </dd>
      </div>
      <div>
        <dt>This account can be used as : </dt>
        <dd>
          <tag class="green" v-if="canBeBenef">Beneficiary</tag>
          <tag class="outline-green" v-if="canBeSender">Sender</tag>
          <tag v-if="!canBeBenef && !canBeSender">None</tag>
        </dd>
      </div>
    </dl>
    <h3>{{ accountType }} Particulars</h3>
    <dl v-if="isCompany">
      <div>
        <dt>Email Address</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.email" v-model.trim="editedAccount.email" /></label></dd>
        <dd v-else>{{ account.email }}</dd>
      </div>
      <div v-if="account.mobile_number || isEditMode">
        <dt>Contact Number (Mobile)</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.mobile_number" v-model.trim="editedAccount.mobile_number" /></label></dd>
        <dd v-else>{{ account.mobile_number }}</dd>
      </div>
      <div v-if="account.phone_number || isEditMode">
        <dt>Phone Number (Office)</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.phone_number" v-model.trim="editedAccount.phone_number" /></label></dd>
        <dd v-else>{{ account.phone_number }}</dd>
      </div>
      <h4>Registration</h4>
      <div>
        <dt>Registered Name</dt>
        <dd v-if="isEditMode && canEditIdentity"><label><vs-input :placeholder="account.registered_name" v-model.trim="editedAccount.registered_name" /></label></dd>
        <dd v-else>{{ account.registered_name }}</dd>
      </div>
      <div v-if="account.trading_name || isEditMode">
        <dt>Trading Name</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.trading_name" v-model.trim="editedAccount.trading_name" /></label></dd>
        <dd v-else>{{ account.trading_name }}</dd>
      </div>
      <div v-if="account.registration_type || isEditMode">
        <!-- Not editable, as we always send 'Trade license' at creation. Values yet to be defined by BE -->
        <dt>License Type</dt>
        <dd>{{ account.registration_type }}</dd>
      </div>
      <div v-if="account.registration_number || isEditMode">
        <dt>Registration Number</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.registration_number" v-model.trim="editedAccount.registration_number" /></label></dd>
        <dd v-else>{{ account.registration_number }}</dd>
      </div>
      <div v-if="account.registration_date || isEditMode">
        <dt>Date of Incorporation</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.registration_date" type="date" v-model.trim="editedAccount.registration_date" /></label></dd>
        <dd v-else>{{ account.registration_date }}</dd>
      </div>
      <div v-if="account.registration_country || isEditMode">
        <dt>Country of Incorporation</dt>
        <dd v-if="isEditMode">
          <label>
            <vs-select v-model="editedAccount.registration_country" autocomplete>
              <vs-select-item v-for="item in countryList" :key="item.code" :value="item.code" :text="item.name" />
            </vs-select>
          </label>
        </dd>
        <dd v-else>{{ account.registration_country }}</dd>
      </div>
      <div v-if="account.business_nature || isEditMode">
        <dt>Business Nature</dt>
        <dd v-if="!isEditMode">{{ BUSINESS_NATURE_LIST[account.business_nature].display }}</dd>
        <dd v-else>
          <vs-select v-model="editedAccount.business_nature" autocomplete v-if="isEditMode">
            <vs-select-item v-for="item in Object.values(BUSINESS_NATURE_LIST)" :key="item.val" :value="item.val" :text="item.display" />
          </vs-select>
        </dd>
      </div>
      <h4 v-if="account.address || account.zip_code || account.city || account.country_code || isEditMode">Address</h4>
      <div v-if="account.address || account.zip_code || account.city || account.country_code || isEditMode">
        <dt>Address</dt>
        <dd v-if="account.address && !isEditMode">{{ account.address }}</dd>
        <dd v-if="account.zip_code && !isEditMode">{{ account.zip_code }}</dd>
        <dd v-if="account.city && !isEditMode">{{ account.city }}</dd>
        <dd v-if="account.province_state && !isEditMode">{{ account.province_state }}</dd>
        <dd v-if="account.country_code && !isEditMode">{{ account.country_code }}</dd>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.address" v-model.trim="editedAccount.address" /></label></dd>
        <dd v-if="isEditMode"><label>Zip Code<vs-input :placeholder="account.zip_code" v-model.trim="editedAccount.zip_code" /></label></dd>
        <dd v-if="isEditMode"><label>City<vs-input :placeholder="account.city" v-model.trim="editedAccount.city" /></label></dd>
        <dd v-if="isEditMode"><label>Province/State<vs-input :placeholder="account.province_state" v-model.trim="editedAccount.province_state" /></label></dd>
        <dd v-if="isEditMode">
          <label>
            Country
            <vs-select v-model="editedAccount.country_code" autocomplete>
              <vs-select-item v-for="item in countryList" :key="item.code" :value="item.code" :text="item.name" />
            </vs-select>
          </label>
        </dd>
      </div>
    </dl>
    <dl v-else-if="!isCompany">
      <div>
        <dt>First Name</dt>
        <dd v-if="isEditMode && canEditIdentity"><label><vs-input :placeholder="account.first_name" v-model.trim="editedAccount.first_name" /></label></dd>
        <dd v-else>{{ account.first_name }}</dd>
      </div>
      <div>
        <dt>Last Name</dt>
        <dd v-if="isEditMode && canEditIdentity"><label><vs-input :placeholder="account.last_name" v-model.trim="editedAccount.last_name" /></label></dd>
        <dd v-else>{{ account.last_name }}</dd>
      </div>
      <div v-if="account.email || isEditMode">
        <dt>Email Address</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.email" v-model.trim="editedAccount.email" type="email" /></label></dd>
        <dd v-else>{{ account.email }}</dd>
      </div>
      <div v-if="account.mobile_number || isEditMode">
        <dt>Contact number (Mobile)</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.mobile_number" v-model.trim="editedAccount.mobile_number" /></label></dd>
        <dd v-else>{{ account.mobile_number }}</dd>
      </div>
      <div v-if="account.occupation || isEditMode">
        <dt>Occupation</dt>
        <dd v-if="isEditMode">
          <label>
            <vs-select v-model="editedAccount.occupation" autocomplete>
              <vs-select-item v-for="item in Object.values(OCCUPATION_LIST)" :key="item.val + 'occDetails'" :value="item.val" :text="item.display" />
            </vs-select>
          </label>
        </dd>
        <dd v-else>{{ OCCUPATION_LIST[account.occupation].display }}</dd>
      </div>
      <div v-if="account.gender || isEditMode">
        <dt>Gender</dt>
        <dd v-if="isEditMode">
          <label>
            <vs-select v-model="editedAccount.gender">
              <vs-select-item value="male" text="Male" />
              <vs-select-item value="female" text="Female" />
            </vs-select>
          </label>
        </dd>
        <dd v-else>{{ account.gender }}</dd>
      </div>
      <h4 v-if="account.address || account.zip_code || account.city || account.country_code || account.province_state || isEditMode">Address</h4>
      <div v-if="account.address || account.zip_code || account.city || account.country_code || account.province_state || isEditMode">
        <dt>Address</dt>
        <dd v-if="account.address && !isEditMode">{{ account.address }}</dd>
        <dd v-if="account.zip_code && !isEditMode">{{ account.zip_code }}</dd>
        <dd v-if="account.city && !isEditMode">{{ account.city }}</dd>
        <dd v-if="account.province_state && !isEditMode">{{ account.province_state }}</dd>
        <dd v-if="account.country_code && !isEditMode">{{ account.country_code }}</dd>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.address" v-model.trim="editedAccount.address" /></label></dd>
        <dd v-if="isEditMode"><label>Zip Code<vs-input :placeholder="account.zip_code" v-model.trim="editedAccount.zip_code" /></label></dd>
        <dd v-if="isEditMode"><label>City<vs-input :placeholder="account.city" v-model.trim="editedAccount.city" /></label></dd>
        <dd v-if="isEditMode"><label>Province/State<vs-input :placeholder="account.province_state" v-model.trim="editedAccount.province_state" /></label></dd>
        <dd v-if="isEditMode">
          <label>
            Country
            <vs-select v-model="editedAccount.country_code" autocomplete>
              <vs-select-item v-for="item in countryList" :key="item.code" :value="item.code" :text="item.name" />
            </vs-select>
          </label>
        </dd>
      </div>

      <h4>Identification</h4>
      <div v-if="account.nationality || isEditMode">
        <dt>Nationality</dt>
        <dd v-if="isEditMode">
          <label>
            <vs-select v-model.trim="editedAccount.nationality" autocomplete>
              <vs-select-item v-for="item in countryList" :key="item.code" :value="item.code" :text="item.name" />
            </vs-select>
          </label>
        </dd>
        <dd v-else>{{ account.nationality }}</dd>
      </div>
      <div v-if="account.date_of_birth || (isEditMode && canEditIdentity)">
        <dt>Date of Birth</dt>
        <dd v-if="isEditMode && canEditIdentity"><label><vs-input type="date" :placeholder="account.date_of_birth" v-model.trim="editedAccount.date_of_birth" :max="TODAY" /></label></dd>
        <dd v-else>{{ account.date_of_birth }}</dd>
      </div>
      <div>
        <dt>ID Type</dt>
        <dd v-if="isEditMode || isEditMode">
          <label>
            <vs-select v-model="editedAccount.identification_type" autocomplete>
              <vs-select-item v-for="item in ID_TYPES" :key="item.value" :value="item.value" :text="item.display" />
            </vs-select>
          </label>
        </dd>
        <dd v-else>{{ account.identification_type ? account.identification_type : '/' }}</dd>
      </div>
      <div>
        <dt>ID Number</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="account.identification_number" v-model.trim="editedAccount.identification_number" /></label></dd>
        <dd v-else>{{ account.identification_number ? account.identification_number : '/' }}</dd>
      </div>
      <div v-if="account.identification_issuer || isEditMode">
        <dt>ID country of issuance</dt>
        <dd v-if="isEditMode">
          <label>
            <vs-select v-model="editedAccount.identification_issuer" autocomplete>
              <vs-select-item v-for="item in countryList" :key="item.code" :value="item.code" :text="item.name" />
            </vs-select>
          </label>
        </dd>
        <dd v-else>{{ account.identification_issuer }}</dd>
      </div>
      <div v-if="account.identification_issue_date || isEditMode">
        <dt>Issue date</dt>
        <dd v-if="isEditMode"><label><vs-input type="date" :placeholder="account.identification_issue_date" v-model.trim="editedAccount.identification_issue_date" /></label></dd>
        <dd v-else>{{ account.identification_issue_date }}</dd>
      </div>
      <div v-if="account.identification_expiry_date || isEditMode">
        <dt>Expiry date</dt>
        <dd v-if="isEditMode"><label><vs-input type="date" :placeholder="account.identification_expiry_date" v-model.trim="editedAccount.identification_expiry_date" /></label></dd>
        <dd v-else>{{ account.identification_expiry_date }}</dd>
      </div>
    </dl>
    <dl>
      <div>
        <dt>Account ID (Triple-A)</dt>
        <dd>{{ account.id }}</dd>
      </div>
      <div>
        <dt>Account Creation date</dt>
        <dd>{{ new Date(account.created_at) }}</dd>
      </div>
      <div>
        <dt>Account Last Updated date</dt>
        <dd>{{ new Date(account.updated_at) }}</dd>
      </div>
    </dl>
    <h3 v-if="bankDetails && bankDetails.length > 0">Payment Details</h3>
    <dl v-for="(bankAcc, index) in bankDetails" :key="bankAcc.id">
      <div>
        <dt>Account Name</dt>
        <dd v-if="isEditMode && canEditIdentity"><label><vs-input disabled :value="isCompany ? `${editedAccount.registered_name}` : `${editedAccount.first_name} ${editedAccount.last_name}`" /></label></dd>
        <dd v-else>{{ bankAcc.account.account_name }}</dd>
      </div>
      <!-- removing alias as it is a default value set by BE -->
      <!-- <div v-if="bankAcc.account.alias">
        <dt>Alias</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="bankAcc.account.alias" v-model="editedBankDetails[index].account.alias" /></label></dd>
        <dd v-else>{{ bankAcc.account.alias }}</dd>
      </div> -->
      <div v-if="bankAcc && (bankAcc.external_id || isEditMode)">
        <dt>Your Reference ID</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="bankAcc.external_id" v-model.trim="editedBankDetails[index].external_id" /></label></dd>
        <dd v-else>{{ bankAcc.external_id }}</dd>
      </div>
      <div>
        <dt>Currency of Account</dt>
        <dd>{{ bankAcc.currency }}</dd>
      </div>
      <div>
        <dt>Country of Bank</dt>
        <dd>{{ bankAcc.country_code }}</dd>
      </div>
      <div v-if="bankAcc.account.account_type || isEditMode">
        <dt>Bank Account Format</dt>
        <dd v-if="isEditMode">
          <label>
            <vs-select v-model="editedBankDetails[index].account.account_type" autocomplete>
              <vs-select-item v-for="item in BANK_ACCOUNT_FORMATS" :key="item.val" :value="item.val" :text="item.display" />
            </vs-select>
          </label>
        </dd>
        <dd v-else>{{ bankAcc.account.account_type }}</dd>
      </div>
      <div v-if="bankAcc.account.bank_account_type || isEditMode">
        <dt>Bank Account Type</dt>
        <dd v-if="isEditMode">
          <label>
            <vs-select v-model="editedBankDetails[index].account.bank_account_type" autocomplete>
              <vs-select-item v-for="item in BANK_ACCOUNT_TYPES" :key="item.val" :value="item.val" :text="item.display" />
            </vs-select>
          </label>
        </dd>
        <dd v-else>{{ bankAcc.account.bank_account_type }}</dd>
      </div>
      <div v-if="bankAcc.account.account_number || isEditMode">
        <dt>Account Number</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="bankAcc.account.account_number" v-model.trim="editedBankDetails[index].account.account_number" /></label></dd>
        <dd v-else>{{ bankAcc.account.account_number }}</dd>
      </div>
      <div v-if="bankAcc.account.routing_type">
        <dt>Routing Type</dt>
        <dd v-if="isEditMode">
          <label>
            <vs-select v-model="editedBankDetails[index].account.routing_type" autocomplete>
              <vs-select-item v-for="item in ROUTING_TYPES" :key="item.val" :value="item.val" :text="item.display" />
            </vs-select>
          </label>
        </dd>
        <dd v-else>{{ bankAcc.account.routing_type }}</dd>
      </div>
      <div v-if="bankAcc.account.routing_code || isEditMode">
        <dt>Routing Code ({{ bankAcc.account.routing_type }} code)</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="bankAcc.account.routing_code" v-model.trim="editedBankDetails[index].account.routing_code" /></label></dd>
        <dd v-else>{{ bankAcc.account.routing_code }}</dd>
      </div>
      <div v-if="bankAcc.account.branch_code || isEditMode">
        <dt>Bank Branch Code</dt>
        <dd v-if="isEditMode"><label><vs-input :placeholder="bankAcc.account.branch_code" v-model.trim="editedBankDetails[index].account.branch_code" /></label></dd>
        <dd v-else>{{ bankAcc.acount.branch_code }}</dd>
      </div>
      <div>
        <dt>Account ID (Triple-A)</dt>
        <dd>{{ bankAcc.id }}</dd>
      </div>
      <div>
        <dt>Account Creation date</dt>
        <dd>{{ new Date(bankAcc.created_at) }}</dd>
      </div>
      <div>
        <dt>Account Last Updated date</dt>
        <dd>{{ new Date(bankAcc.updated_at) }}</dd>
      </div>
    </dl>
  </article>
</template>
<script>
import { FIAT_BANK_ACCOUNT_FORMATS, FIAT_ROUTING_TYPES, PAYOUT_ACCOUNT_TYPES, FIAT_ID_TYPE, FIAT_BANK_ACCOUNT_TYPES, FIAT_ACCOUNT_OCCUPATION, FIAT_ACCOUNT_BUSINESS_NATURE } from '../constants';
import Tag from '../atoms/Tag.vue';
export default {
  components: { Tag },
  props: {
    account: {
      type: Object,
    },
    editedAccount: {
      type: Object,
    },
    isCompany: {
      type: Boolean,
      default: true
    },
    bankDetails: {
      type: Array,
      default: () => []
    },
    editedBankDetails: {
      type: Array,
      default: () => [
        {
          'account': {
            'network': '',
            'account_name': '',
            'account_type': '',
            'routing_code': '',
            'routing_type': '',
            'account_number': ''
          },
          'external_id': null,
          'archived': false,
          'created_at': '',
          'updated_at': ''
        }
      ]
    },
    shouldDisplayKycUploadButton: {
      type: Boolean,
      default: false,
    },
    canEditIdentity: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    countryList: {
      type: Array,
    },
  },
  data() {
    return {
      ACCOUNT_TYPES: PAYOUT_ACCOUNT_TYPES,
      BANK_ACCOUNT_FORMATS: FIAT_BANK_ACCOUNT_FORMATS,
      BANK_ACCOUNT_TYPES: FIAT_BANK_ACCOUNT_TYPES,
      ROUTING_TYPES: FIAT_ROUTING_TYPES,
      ID_TYPES: FIAT_ID_TYPE,
      OCCUPATION_LIST: FIAT_ACCOUNT_OCCUPATION,
      BUSINESS_NATURE_LIST: FIAT_ACCOUNT_BUSINESS_NATURE,
      TODAY: new Date().toISOString().split('T')[0],
    };
  },
  methods: {
    handleKycClick() {
      this.$emit('kyc-click');
    },
  },
  computed: {
    accountType() {
      return this.isCompany ? PAYOUT_ACCOUNT_TYPES.CORPORATE.display : PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.display;
    },
    canBeBenef() {
      return (this.bankDetails
        && this.bankDetails.length > 0);
    },
    canBeSender() {
      return ((this.accountType === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.display
        && this.account.verification_status === 'confirmed')
        || this.accountType === PAYOUT_ACCOUNT_TYPES.CORPORATE.display);
    },
  },
};
</script>
<style lang="scss">
.fiat-account-details-wrapper {
  padding-left: 16px;
  padding-top: 16px;
  max-width: 450px;

  h3,
  h4 {
    color: $color-primary-light;
    font-weight: bold;
    font-size: 18px;
  }

  h4 {
    margin-top: $margin-l;
  }

  button.btn {
    &.edit {
      &::before {
        height: 16px;
        width: 16px;
      }

      padding-left: 32px;
      min-height: 32px;
    }
  }

  dl {
    margin-bottom: $margin-l;

    div {
      margin-bottom: 6px;

      dt {
        color: $color-text-light;
      }

      dd {
        font-weight: bold;

        .tag {
          margin-right: 6px;
          margin-top: 3px;
          font-weight: normal;
          display: inline-flex;
          width: fit-content;
        }

        button.btn.icon-btn.plus {
          margin-left: $margin-xs;
          height: 26px;
          min-height: 26px;
          padding-left: 36px;
          padding-right: 6px;
          padding-top: 0px;
          padding-bottom: 0px;

          &::before {
            top: 1px;
          }
        }

        label {
          display: flex;
          flex-direction: column;
          max-width: 280px;
          color: #a0a0a0;
          font-weight: normal;

          input {
            border: 1px solid #a0a0a0;
            border-radius: 3px;
            padding: 4px;
            color: $color-text;
          }

          .vs-con-input-label,
          .con-select {
            width: 100%
          }
        }
      }
    }
  }
}

.fiat-account-details-wrapper.con-vs-popup {
  max-width: 450px;
}
</style>